/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
} from 'react';
import { AuthContext } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, Link } from 'react-router-dom'; // Added Link
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark, oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import ErrorBoundary from './ErrorBoundary';
import { retryWithBackoff, validateAIResponse, getErrorMessage, getFallbackResponse } from '../utils/aiHelpers';
import logger from '../utils/logger';
import { MicrophoneIcon, StopIcon, ComputerDesktopIcon } from '@heroicons/react/24/outline';
import { exportChatAsPdf } from '../utils/pdfExport';

/* ---------- helpers ---------- */
const generateId = () =>
  `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// IMPORTANT: You'll need to pass the text of the currently viewed content block to this component.
// For example, if MakerstateAgent is rendered by a parent component that manages chapter content display,
// that parent should pass props like `currentContentBlockText`, `currentFeedback`, and `currentBlockIndex`.
const MakerstateAgent = ({ currentContentBlockText, currentFeedback, currentBlockIndex }) => {
  const { token, isAuthenticated, user } = useContext(AuthContext); // Ensure 'user' is destructured
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const [chatMode, setChatMode] = useState('text'); // 'text' or 'audio'
  const [isListening, setIsListening] = useState(false); // From original
  const recognitionRef = useRef(null); // From original
  const synthRef = useRef(null); // For TTS // From original
  const location = useLocation(); // From original & snippet
  const [contextualPrompts, setContextualPrompts] = useState([]); // From original
  const [isSpeechRecognitionSupported, setIsSpeechRecognitionSupported] = useState(false);
  const listeningStoppedByUserRef = useRef(false);
  const chatModeRef = useRef(chatMode);
  const prevChatModeRef = useRef(chatMode);

  useEffect(() => {
    chatModeRef.current = chatMode;
  }, [chatMode]);

  useEffect(() => {
    // Initialize SpeechSynthesis and add cleanup
    synthRef.current = window.speechSynthesis;
    const currentSynth = synthRef.current;
    return () => {
      if (currentSynth?.speaking) {
        currentSynth.cancel();
      }
    };
  }, []);

  // Text-to-Speech function
  const speak = useCallback((text) => {
    if (!text || !synthRef.current?.speak) return;
    if (chatModeRef.current !== 'audio') return;

    if (synthRef.current.speaking) {
      synthRef.current.cancel();
    }

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 1.1;
    utterance.pitch = 1;

    if (synthRef.current.paused) {
      synthRef.current.resume();
    }
    synthRef.current.speak(utterance);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setContextualPrompts([]);
      return;
    }

    const path = location.pathname;
    if (path === '/profile' || path.startsWith('/profile/')) {
      setContextualPrompts([
        "What's my next recommended module?",
        "Summarize my progress so far.",
        "Suggest areas for improvement based on my work."
      ]);
    } else if (path.startsWith('/chapter/')) {
      setContextualPrompts([
        "Can you elaborate on the current topic?",
        "Give me an example related to this chapter.",
        "What are the key learning objectives here?"
      ]);
    } else if (path.startsWith('/feedback/')) {
      setContextualPrompts([
        "Can you explain this feedback in simpler terms?",
        "What are the key takeaways from my feedback?",
        "How can I improve based on this feedback?"
      ]);
    } else if (path.startsWith('/admin')) {
      setContextualPrompts([
        "How do I manage chapters?",
        "Where can I see user statistics?",
      ]);
    } else {
      setContextualPrompts([]);
    }
  }, [location.pathname, isAuthenticated]);

  /* ------------------------------------------------------------
   * call OpenAI via your backend
   * ---------------------------------------------------------- */
  const makeApiCall = async (historyForAPI) => {
    setIsTyping(true);
    // Capture the last user message for retry functionality
    const lastUserMessage = historyForAPI
      .filter(msg => msg.role === 'user')
      .slice(-1)[0]?.content || '';
    setUserInput('');

    // Determine current page and chapter ID
    const currentPath = location.pathname;
    let chapterId = null;
    
    if (currentPath.startsWith('/chapter/')) {
      chapterId = currentPath.split('/').pop();
    } else if (currentPath.startsWith('/feedback/')) {
      chapterId = currentPath.split('/').pop();
    }

    // Determine current page type
    const currentPage = currentPath === '/profile' || currentPath.startsWith('/profile/') 
      ? 'profile' 
      : currentPath.startsWith('/chapter/') 
      ? 'chapter' 
      : currentPath.startsWith('/feedback/') 
      ? 'feedback' 
      : currentPath.startsWith('/admin') 
      ? 'admin' 
      : 'unknown';

    const assistantMessageId = generateId();
    let retryCount = 0;

    try {
      const response = await retryWithBackoff(
        async () => {
          const res = await fetch(
            `${import.meta.env.VITE_API_URL}/api/chat/message`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                // systemPrompt is now built entirely by the backend using templates
                systemPrompt: '',
                messages: historyForAPI,
                chapterId: chapterId || null,
                currentPage,
                currentBlockIndex: currentBlockIndex !== undefined ? currentBlockIndex : null,
                currentContentBlockText: currentContentBlockText || null,
                currentFeedback: currentFeedback || null,
                sessionId: currentSessionId || null,
              }),
            }
          );

          if (!res.ok) {
            const errorData = await res.json().catch(() => ({}));
            const error = new Error(errorData.error || `HTTP ${res.status}`);
            error.status = res.status;
            error.code = errorData.code;
            error.retryAfter = errorData.retryAfter;
            error.requestId = errorData.requestId;
            throw error;
          }

          return res;
        },
        {
          maxRetries: 3,
          initialDelay: 1000,
          shouldRetry: (error) => {
            // Do not retry app-enforced rate limits (15-minute window), only transient upstream 429s.
            if (error.status === 429 && (error.code === 'RATE_LIMIT_EXCEEDED' || error.retryAfter >= 300)) {
              return false;
            }

            const shouldRetry = error.status === 429 || (error.status >= 500 && error.status < 600);
            if (shouldRetry) retryCount++;
            return shouldRetry;
          }
        }
      );

      const data = await response.json();

      // Capture session ID returned by backend
      if (data.sessionId) {
        setCurrentSessionId(data.sessionId);
      }

      // Validate response
      const validation = validateAIResponse(data, 'message');
      if (!validation.valid) {
        throw new Error(validation.error || 'Invalid response format');
      }

      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: '', id: assistantMessageId },
      ]);
      await streamAssistantMessage(validation.data, assistantMessageId);
    } catch (err) {
      logger.error('Error sending message:', err);
      const errorMsg = getErrorMessage(err);
      const fallbackMsg = retryCount > 0 
        ? `${errorMsg} (Retried ${retryCount} time${retryCount > 1 ? 's' : ''})`
        : errorMsg;
      
      setMessages((prev) => [
        ...prev,
        { 
          role: 'assistant', 
          content: fallbackMsg,
          id: assistantMessageId,
          isError: true,
          canRetry: true,
          originalPrompt: lastUserMessage // Store the last user message for retry
        },
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  // Helper function to stream the assistant's message
  const streamAssistantMessage = useCallback(async (fullText, messageId) => {
    return new Promise((resolve) => {
      let charIndex = 0;
      const streamingSpeed = 15; 

      if (fullText.length === 0) {
        setMessages(prevMessages => prevMessages.map(msg => 
          msg.id === messageId ? { ...msg, content: '...' } : msg
        ));
        if (chatModeRef.current === 'audio') speak('...');
        resolve();
        return;
      }

      const intervalId = setInterval(() => {
        if (charIndex < fullText.length) {
          setMessages(prevMessages => {
            return prevMessages.map(msg => {
              if (msg.id === messageId && msg.role === 'assistant') {
                return {
                  ...msg,
                  content: fullText.substring(0, charIndex + 1),
                };
              }
              return msg;
            });
          });
          charIndex++;
        } else {
          clearInterval(intervalId);
          if (chatModeRef.current === 'audio') {
            speak(fullText);
          }
          resolve();
        }
      }, streamingSpeed);
    });
  }, [speak]);

  const streamAssistantMessageRef = useRef(streamAssistantMessage);
  useEffect(() => {
    streamAssistantMessageRef.current = streamAssistantMessage;
  }, [streamAssistantMessage]);

  useEffect(() => {
    const streamInitialMessage = async (text) => {
      setIsTyping(true);
      const newMessageId = generateId();
      setMessages([{ role: 'assistant', content: '', id: newMessageId }]);
      await (streamAssistantMessageRef.current || streamAssistantMessage)(text, newMessageId);
      setIsTyping(false);
    };

    if (!isAuthenticated) {
      streamInitialMessage('Please log in to use the Makerstate Agent.');
      return;
    }

    if (user?.role === 'student') {
      streamInitialMessage("Upgrade to Creator Pro to unlock Oberon AI and get personalized assistance with your creative business!");
      return;
    }

    const path = location.pathname;
    const userName = user?.name || 'there';

    const getWelcomeMessage = () => {
      if (path.startsWith('/chapter/'))  return `Hi ${userName}! Please feel free to ask me anything about the chapter and its exercises.`;
      if (path.startsWith('/feedback/')) return `Hi ${userName}! Please feel free to ask me anything about your feedback.`;
      return `Hi ${userName}! How may I assist you today?`;
    };

    // Skip session restore for admin pages
    if (path.startsWith('/admin')) {
      setCurrentSessionId(null);
      streamInitialMessage(getWelcomeMessage());
      return;
    }

    // Try to restore the most recent session for this page context
    const tryRestoreOrWelcome = async () => {
      if ((user?.settings?.saveChatHistory ?? false) && token) {
        try {
          let chapterId = null;
          if (path.startsWith('/chapter/'))  chapterId = path.split('/').pop();
          else if (path.startsWith('/feedback/')) chapterId = path.split('/').pop();

          const query = chapterId ? `chapterId=${chapterId}` : 'chapterId=none';
          const listRes = await fetch(
            `${import.meta.env.VITE_API_URL}/api/chat/sessions?${query}&limit=1`,
            { headers: { Authorization: `Bearer ${token}` } }
          );

          if (listRes.ok) {
            const listData = await listRes.json();
            if (listData.sessions?.length > 0) {
              const sessRes = await fetch(
                `${import.meta.env.VITE_API_URL}/api/chat/sessions/${listData.sessions[0]._id}`,
                { headers: { Authorization: `Bearer ${token}` } }
              );
              if (sessRes.ok) {
                const sessData = await sessRes.json();
                setCurrentSessionId(sessData.session._id);
                setMessages(
                  sessData.session.messages.map(m => ({
                    role:    m.role,
                    content: m.content,
                    id:      generateId()
                  }))
                );
                return; // Session restored — skip welcome message
              }
            }
          }
        } catch (e) {
          logger.warn('Could not restore chat session:', e.message);
        }
      }

      // No session found (or saving disabled) — show welcome message
      setCurrentSessionId(null);
      streamInitialMessage(getWelcomeMessage());
    };

    tryRestoreOrWelcome();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- omit streamAssistantMessage so toggling audio/text does not reset conversation
  }, [isAuthenticated, user, location.pathname]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Check browser support on mount - don't show error, just check availability
  useEffect(() => {
    const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
    setIsSpeechRecognitionSupported(!!SpeechRecognitionAPI);
    if (!SpeechRecognitionAPI) {
      logger.info('Speech recognition API not detected in this browser.');
    }
  }, []);

  // Speech Recognition Setup - improved browser compatibility
  useEffect(() => {
    // Check for browser support
    const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognitionAPI) {
      // No speech recognition (e.g. some Chrome setups) - allow audio mode for TTS only; mic button won't show
      setIsSpeechRecognitionSupported(false);
      return;
    }
    
    setIsSpeechRecognitionSupported(true);

    // Clean up existing recognition if any
    if (recognitionRef.current) {
      try {
        recognitionRef.current.abort();
      } catch (e) {
        // Ignore errors during cleanup
      }
    }

    try {
      recognitionRef.current = new SpeechRecognitionAPI();
      const recognition = recognitionRef.current;

      // Browser-specific configuration
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = 'en-US';
      
      // Safari-specific: set maxAlternatives
      if (recognition.maxAlternatives !== undefined) {
        recognition.maxAlternatives = 1;
      }

      recognition.onstart = () => {
        // Safari can fire onstart after user clicked stop; ignore so mic icon doesn't glitch
        if (listeningStoppedByUserRef.current) return;
        listeningStoppedByUserRef.current = false;
        setIsListening(true);
        logger.info('Speech recognition started');
      };

      recognition.onresult = (event) => {
        let interimTranscript = '';
        let finalTranscript = '';
        
        // Handle results more robustly
        for (let i = event.resultIndex; i < event.results.length; ++i) {
          const transcript = event.results[i][0]?.transcript || '';
          if (event.results[i].isFinal) {
            finalTranscript += transcript + ' ';
          } else {
            interimTranscript += transcript;
          }
        }
        
        // Update input with both final and interim results
        const combined = (finalTranscript + interimTranscript).trim();
        if (combined) {
          setUserInput(combined);
        }
      };

      recognition.onerror = (event) => {
        logger.error('Speech recognition error:', event.error);
        setIsListening(false);
        
        // Handle different error types
        switch (event.error) {
          case 'not-allowed':
            alert('Microphone access was denied. Please allow microphone access in your browser settings and refresh the page.');
            setChatMode('text');
            break;
          case 'no-speech':
            // This is normal when user doesn't speak, just stop listening
            setIsListening(false);
            break;
          case 'audio-capture':
            alert('No microphone found. Please ensure a microphone is connected and try again.');
            setChatMode('text');
            break;
          case 'network':
            alert('Network error occurred. Please check your connection and try again.');
            break;
          case 'aborted':
            // User or system aborted, this is fine
            setIsListening(false);
            break;
          default:
            logger.warn('Speech recognition error:', event.error);
            setIsListening(false);
        }
      };

      recognition.onend = () => {
        // Safari can fire onend late or out of order; always set false so mic icon updates
        listeningStoppedByUserRef.current = false;
        setIsListening(false);
        logger.info('Speech recognition ended');
      };

      recognition.onnomatch = () => {
        // No speech was detected, this is normal
        setIsListening(false);
      };

    } catch (error) {
      logger.error('Error initializing speech recognition:', error);
      if (chatMode === 'audio') {
        alert('Failed to initialize speech recognition. Please try refreshing the page or use text mode.');
        setChatMode('text');
      }
    }

    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort();
        } catch (e) {
          // Ignore cleanup errors
        }
      }
    };
  }, [chatMode]);

  const handleToggleListen = () => {
    if (!recognitionRef.current || chatMode !== 'audio') {
      // If in audio mode but recognition isn't available, silently switch to text
      if (chatMode === 'audio' && !recognitionRef.current) {
        setChatMode('text');
      }
      return;
    }

    if (isListening) {
      // Stop listening
      listeningStoppedByUserRef.current = true;
      setIsListening(false);
      try {
        recognitionRef.current.stop();
      } catch (error) {
        logger.error("Error stopping speech recognition:", error);
      }
    } else {
      // Start listening
      listeningStoppedByUserRef.current = false;
      setUserInput('');
      const rec = recognitionRef.current;
      const state = typeof rec?.state === 'string' ? rec.state : '';
      const alreadyRunning = state === 'listening' || state === 'starting';
      if (alreadyRunning) {
        try {
          rec.stop();
        } catch (e) {
          // Ignore if already stopped
        }
        setTimeout(() => {
          try {
            if (recognitionRef.current) recognitionRef.current.start();
          } catch (error) {
            handleRecognitionStartError(error);
          }
        }, 100);
      } else {
        try {
          rec.start();
        } catch (error) {
          handleRecognitionStartError(error);
        }
      }
    }
  };

  const handleRecognitionStartError = (error) => {
    logger.error("Error starting speech recognition:", error);
    setIsListening(false);
    
    if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
      alert('Microphone access was denied. Please allow microphone access in your browser settings and refresh the page.');
      setChatMode('text');
    } else if (error.name === 'InvalidStateError') {
      // Recognition is already running or in an invalid state
      // Try to reset it
      try {
        recognitionRef.current.abort();
        setTimeout(() => {
          try {
            recognitionRef.current.start();
          } catch (retryError) {
            logger.error("Error retrying speech recognition:", retryError);
            setIsListening(false);
          }
        }, 200);
      } catch (resetError) {
        logger.error("Error resetting speech recognition:", resetError);
        setIsListening(false);
      }
    } else {
      alert('Failed to start speech recognition. Please try again or use text mode.');
    }
  };
  
  const toggleChatMode = () => {
    setChatMode(prevMode => {
      const newMode = prevMode === 'text' ? 'audio' : 'text';

      if (newMode === 'text') {
        listeningStoppedByUserRef.current = true;
        if (isListening && recognitionRef.current) {
          try {
            recognitionRef.current.stop();
          } catch (e) {
            // Ignore
          }
          setIsListening(false);
        }
        if (synthRef.current?.speaking) {
          synthRef.current.cancel();
        }
      } else {
        listeningStoppedByUserRef.current = false;
      }

      return newMode;
    });
  };

  // When user switches to audio, speak the last assistant message (Firefox needs user gesture; Safari welcome may have been skipped)
  useEffect(() => {
    if (chatMode !== 'audio' || messages.length === 0 || isTyping) return;
    const prev = prevChatModeRef.current;
    prevChatModeRef.current = chatMode;
    if (prev !== 'audio') {
      const lastAssistant = [...messages].reverse().find(m => m.role === 'assistant' && !m.isError && m.content);
      if (lastAssistant?.content?.trim()) {
        speak(lastAssistant.content.trim());
      }
    }
  }, [chatMode, messages, isTyping, speak]);

  const handleSendMessage = async (messageContent = userInput) => {
    const textToSend = typeof messageContent === 'string' ? messageContent.trim() : userInput.trim();
    if (!textToSend || !isAuthenticated || isTyping) return;

    // Stop listening if in audio mode
    if (isListening && recognitionRef.current) {
      try {
        recognitionRef.current.stop();
        setIsListening(false);
      } catch (e) {
        // Ignore errors
      }
    }

    const userMessageId = generateId();
    const newUserMessage = { role: 'user', content: textToSend, id: userMessageId };

    const updatedMessagesWithUser = [...messages, newUserMessage];
    setMessages(updatedMessagesWithUser);

    const messageHistoryForAPI = updatedMessagesWithUser
      .filter(msg => msg.role === 'user' || msg.role === 'assistant')
      .slice(-20)
      .map(m => ({ role: m.role, content: m.content }));

    // Call the standalone makeApiCall function defined above
    makeApiCall(messageHistoryForAPI);
  };

  const handlePromptClick = (prompt) => {
    setUserInput(prompt);
    handleSendMessage(prompt);
  };

  return (
    <ErrorBoundary
      title="Oberon AI Assistant Error"
      message="Oberon encountered an error. Please refresh the page or try again."
    >
      <div className="fixed top-16 right-0 h-[calc(100vh-4rem)] w-1/3 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-200 flex flex-col z-[60] font-body">
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
        <div className="flex flex-col min-w-0">
          <h2 className="text-lg font-body text-gray-900 dark:text-gray-100">Oberon 🧚‍♂️</h2>
          {isAuthenticated && user?.role !== 'student' && (
            <span className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
              Chat history{' '}
              {(user?.settings?.saveChatHistory ?? false) ? (
                <span className="text-green-600 dark:text-green-400">Enabled</span>
              ) : (
                <span>Disabled</span>
              )}
              {' · '}
              <Link to="/account" className="underline hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                Settings
              </Link>
              {messages.filter(m => m.content && !m.isError).length > 1 && (
                <>
                  {' · '}
                  <button
                    onClick={() => {
                      const path = location.pathname;
                      const title = path.startsWith('/chapter/') ? 'Chapter Discussion'
                        : path.startsWith('/feedback/') ? 'Feedback Discussion'
                        : path === '/profile' ? 'Profile'
                        : 'Chat Session';
                      exportChatAsPdf(messages, title);
                    }}
                    className="underline hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  >
                    Download
                  </button>
                </>
              )}
            </span>
          )}
        </div>
        {isAuthenticated && (
          <div className="flex items-center shrink-0">
            <span
              className={'text-xs font-medium min-w-[5rem] inline-block text-gray-500 dark:text-gray-400'
              }
              aria-live="polite"
            >
              {chatMode === 'audio' ? 'Audio' : 'Text'}
            </span>
            <button
              onClick={toggleChatMode}
              title={`Switch to ${chatMode === 'text' ? 'Audio' : 'Text'} Mode`}
              className={`w-9 h-9 flex items-center justify-center rounded-md shrink-0 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors ${
                chatMode === 'audio' ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400' : ''
              }`}
              type="button"
              aria-label={chatMode === 'text' ? 'Switch to Audio mode' : 'Switch to Text mode'}
            >
              {chatMode === 'text' ? (
                <MicrophoneIcon className="w-5 h-5 shrink-0" aria-hidden />
              ) : (
                <ComputerDesktopIcon className="w-5 h-5 shrink-0" aria-hidden />
              )}
            </button>
          </div>
        )}
      </div>

      <div
        className="flex-grow overflow-y-auto p-4 space-y-3"
        role="log"
        aria-live="polite"
        aria-label="Chat messages"
      >
        <AnimatePresence initial={false}>
          {messages.map((msg) => (
            <motion.div
              key={msg.id}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, transition: { duration: 0.15 } }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              layout
            >
              <div
                className={`max-w-[90%] px-3 py-2 rounded-lg shadow-sm ${
                  msg.role === 'user' 
                    ? 'bg-customGreen text-white' 
                    : msg.isError
                    ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200'
                }`}
              >
                {msg.role === 'assistant' ? (
                  <>
                    {msg.isError ? (
                      <div className="flex flex-col">
                        <p className="text-sm text-red-700 dark:text-red-300 mb-2">{msg.content}</p>
                        {msg.canRetry && (
                          <button
                            onClick={() => {
                              // Remove error message and retry
                              setMessages(prev => prev.filter(m => m.id !== msg.id));
                              if (msg.originalPrompt) {
                                handleSendMessage(msg.originalPrompt);
                              }
                            }}
                            className="text-xs bg-red-100 dark:bg-red-900/40 hover:bg-red-200 dark:hover:bg-red-900/60 text-red-800 dark:text-red-200 px-2 py-1 rounded self-start transition-colors"
                          >
                            Retry
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="prose prose-sm max-w-none dark:prose-invert 
                        prose-headings:dark:text-gray-200 prose-p:dark:text-gray-200 
                        prose-table:dark:text-gray-200 prose-th:dark:text-gray-100 
                        prose-td:dark:text-gray-300 prose-a:dark:text-blue-400
                        prose-p:my-2 prose-p:leading-relaxed
                        prose-h1:my-3 prose-h1:leading-tight prose-h2:my-2 prose-h2:leading-tight prose-h3:my-2 prose-h3:leading-tight
                        prose-ul:my-2 prose-ol:my-2 prose-li:my-1
                        prose-blockquote:my-2 prose-blockquote:border-l-4 prose-blockquote:pl-4
                        prose-code:text-sm prose-code:bg-gray-100 prose-code:dark:bg-gray-800 prose-code:px-1 prose-code:py-0.5 prose-code:rounded
                        prose-pre:my-3 prose-pre:bg-gray-900 prose-pre:dark:bg-gray-950
                        prose-strong:font-semibold prose-em:italic">
                        <ReactMarkdown 
                          remarkPlugins={[remarkGfm]}
                          components={{
                            code({ node, inline, className, children, ...props }) {
                              const match = /language-(\w+)/.exec(className || '');
                              const language = match ? match[1] : '';
                              // Check if we're in dark mode
                              const isDark = document.documentElement.classList.contains('dark');
                              // Use syntax highlighter if available, otherwise fallback to regular code
                              if (!inline && language && SyntaxHighlighter && oneDark && oneLight) {
                                return (
                                  <SyntaxHighlighter
                                    style={isDark ? oneDark : oneLight}
                                    language={language}
                                    PreTag="div"
                                    className="rounded-md"
                                    {...props}
                                  >
                                    {String(children).replace(/\n$/, '')}
                                  </SyntaxHighlighter>
                                );
                              }
                              return (
                                <code className={className} {...props}>
                                  {children}
                                </code>
                              );
                            },
                            p({ children }) {
                              return <p className="mb-2 last:mb-0">{children}</p>;
                            },
                            ul({ children }) {
                              return <ul className="mb-2 last:mb-0">{children}</ul>;
                            },
                            ol({ children }) {
                              return <ol className="mb-2 last:mb-0">{children}</ol>;
                            },
                            li({ children }) {
                              return <li className="mb-1">{children}</li>;
                            },
                            blockquote({ children }) {
                              return <blockquote className="my-2 border-l-4 border-gray-300 dark:border-gray-600 pl-4 italic">{children}</blockquote>;
                            },
                            h1({ children }) {
                              return <h1 className="text-xl font-bold mb-2 mt-3 first:mt-0">{children}</h1>;
                            },
                            h2({ children }) {
                              return <h2 className="text-lg font-semibold mb-2 mt-3 first:mt-0">{children}</h2>;
                            },
                            h3({ children }) {
                              return <h3 className="text-base font-semibold mb-2 mt-2 first:mt-0">{children}</h3>;
                            },
                          }}
                        >
                          {msg.content}
                        </ReactMarkdown>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-md whitespace-pre-wrap">{msg.content}</p>
                )}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {isTyping && messages.length > 0 && messages[messages.length -1]?.role === 'user' && (
           <div className="flex justify-start">
             <motion.div 
                initial={{ opacity: 0 }} 
                animate={{ opacity: 1 }} 
                className="max-w-[90%] px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 shadow-sm">
               <p className="text-md animate-pulse">Typing...</p>
             </motion.div>
           </div>
         )}
        <div ref={messagesEndRef} />
      </div>

      {isAuthenticated && (
        <>
          {user?.role === 'student' ? (
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-center">
              <p className="text-md text-gray-600 mb-4">
                Upgrade to Creator Pro to get personalized assistance from Oberon, your AI-powered creative business partner.
              </p>
              <Link
                to="/pricing"
                className="inline-block bg-customGreen text-white px-6 py-2.5 rounded-md font-body"
              >
                View Pro Plan
              </Link>
            </div>
          ) : (
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              {contextualPrompts.length > 0 && (
                <div className="mb-4">
                  <p className="text-md text-gray-500 mb-2">Try asking:</p>
                  <div className="flex flex-wrap gap-2">
                    {contextualPrompts.map((prompt, index) => (
                      <button
                        key={index}
                        onClick={() => handlePromptClick(prompt)}
                        className="text-md bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 px-2.5 py-1 rounded-md transition-colors"
                      >
                        {prompt}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2">
                {chatMode === 'audio' && (
                  <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center">
                    {isSpeechRecognitionSupported && recognitionRef.current && (
                      <button
                        onClick={handleToggleListen}
                        disabled={isTyping}
                        title={isListening ? "Stop listening" : "Start listening"}
                        className={`w-9 h-9 flex items-center justify-center rounded-full flex-shrink-0 ${isListening ? 'bg-red-500 hover:bg-red-600 animate-pulse' : 'bg-blue-500 hover:bg-blue-600'} text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                        type="button"
                        aria-label={isListening ? "Stop listening" : "Start listening"}
                      >
                        {isListening ? (
                          <StopIcon className="w-5 h-5" aria-hidden />
                        ) : (
                          <MicrophoneIcon className="w-5 h-5" aria-hidden />
                        )}
                      </button>
                    )}
                  </div>
                )}
                <input
                  type="text"
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !isTyping) {
                      // Stop listening if active before sending
                      if (isListening && recognitionRef.current) {
                        try {
                          recognitionRef.current.stop();
                          setIsListening(false);
                        } catch (err) {
                          // Ignore errors
                        }
                      }
                      handleSendMessage();
                    }
                  }}
                  placeholder={
                    chatMode === 'audio' 
                      ? (isListening ? "Listening... (click mic to stop)" : "Speak or type...") 
                      : "Ask anything..."
                  }
                  className="text-md flex-grow p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isTyping}
                />
                <button
                  onClick={() => handleSendMessage()}
                  disabled={isTyping || !userInput.trim()}
                  className="text-md bg-black hover:bg-gray-800 text-white px-4 py-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                  type="button"
                >
                  Send
                </button>
              </div>
            </div>
          )}
        </>
      )}
       {!isAuthenticated && (
        <div className="p-4 text-center text-md text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
          Please log in to chat with the Makerstate Agent.
        </div>
      )}
      </div>
    </ErrorBoundary>
  );
};

export default MakerstateAgent;